import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { AuditReadinessRecord } from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { useAuditReadiness } from 'src/pages/Roadmap_v2/roadmap.hooks'
import { normalizeByControlId } from 'src/Utils/globalHelpers'
import { useModalState } from 'src/Modal/ModalStateContext'
import { getRoadmapModalTitle } from 'src/pages/Roadmap_v2/smartchecks/RoadmapSmartChecks.helpers'
import { AuditBotControlsDescription } from 'src/pages/AuditBot/modals/AuditBotControls/AuditBotControlsModal.components'
import { useControlsWithAuditId } from 'src/components/async/model/control/useControls'
import isEqual from 'lodash/isEqual'
import { useDateLimit } from 'src/pages/AccountSettings/AccountSettings.hooks'
import { useRefetchCallback } from 'src/pages/Roadmap_v2/smartchecks/hooks/useRefetchCallback'
import { useInAudit } from '../../../../context/AuditContext'
import { ModalFormId } from '../../ModalForm'
import { DateRange } from '../../../../pages/AuditBot/AuditBot.hooks'
import {
  AuditScanModalContextType,
  ButtonConfig,
  ControlDisplay,
  UseAuditScanModalType,
} from './AuditScanModal.constants'
import {
  createControlTestMap,
  getFilteredControlIds,
  getFilteredControls,
} from './AuditScanModal.helpers'
import { AuditScanModalContext } from './AuditScanModal.context'

export const useAuditScanModalContext = (
  calculateStaleness: boolean,
): AuditScanModalContextType & {
  isLoading: boolean
} => {
  // --------------- get context state ---------------
  const {
    auditId,
    controlsFilter,
    controlsPreselect,
    setFilteredControls,
    setPreselectedControlIds,
    controlTestMap,
    setControlTestMap,
    singleControlId,
    setDateLimit,
  } = useContext(AuditScanModalContext)

  // --------------- Get controls for the current audit ---------------
  const { data: allControls, isLoading: isAllControlsLoading } =
    useControlsWithAuditId(auditId)

  const { data: dateLimitData, isLoading: isDateLimitLoading } = useDateLimit()

  // load each control's latest test results via the audit readiness API.
  const {
    data: auditReadinessData,
    isLoading: isAuditReadinessLoading,
    mutate,
  } = useAuditReadiness({
    auditId,
    calculateStaleness,
  })

  const auditReadinessLookup = useMemo(() => {
    if (!auditReadinessData) return {}

    return normalizeByControlId(auditReadinessData.getAuditReadinessList())
  }, [auditReadinessData])

  // --------------- Update state for controlTestMap, Preselect, Filtered Controls as needed ---------------
  // ----- controlTestMap
  useEffect(() => {
    // Build the controlId → test-result map from each control's OE result.
    const newControlTestMap = createControlTestMap(auditReadinessLookup)
    if (!isEqual(controlTestMap, newControlTestMap))
      setControlTestMap(() => createControlTestMap(auditReadinessLookup))
  }, [auditReadinessLookup, controlTestMap, setControlTestMap])

  useRefetchCallback(mutate, 'AuditScanModal')

  useEffect(() => {
    setFilteredControls(() => {
      if (singleControlId) {
        const control = allControls
          ?.getItemsList()
          .find(
            (control: Control) =>
              control.getId() === singleControlId ||
              control.getMigratedControlId() === singleControlId,
          )
        return control ? [control] : []
      } else {
        const filteredControls = getFilteredControls(
          allControls?.getItemsList() || [],
          controlTestMap,
          controlsFilter,
        )
        setPreselectedControlIds(() =>
          getFilteredControlIds(
            filteredControls,
            controlTestMap,
            controlsPreselect,
          ),
        )
        return filteredControls
      }
    })
  }, [
    allControls,
    controlTestMap,
    controlsFilter,
    controlsPreselect,
    setFilteredControls,
    setPreselectedControlIds,
    singleControlId,
  ])

  useEffect(() => {
    const date = dateLimitData?.getDateLimit()?.toDate()
    if (!date) return
    setDateLimit(date)
  }, [dateLimitData, setDateLimit])

  return {
    ...useContext(AuditScanModalContext),
    isLoading:
      isAllControlsLoading || isAuditReadinessLoading || isDateLimitLoading,
  }
}

export const useAuditScanModal = (
  calculateStaleness: boolean,
): (({
  title,
  description,
  controlsFilter,
  controlsPreselect,
  onBackModalId,
  singleControlId,
  displayConfig,
  dateRange,
}: UseAuditScanModalType) => void) => {
  const { openModal } = useModalState()
  const {
    setAuditId,
    setTitle,
    setDescription,
    setControlsFilter,
    setControlsPreselect,
    setOnBackModalId,
    setSingleControlId,
    setDisplayConfig,
    setDateRange,
    dateLimit,
  } = useAuditScanModalContext(calculateStaleness)

  return useCallback(
    ({
      auditId,
      title,
      description,
      controlsFilter,
      controlsPreselect,
      onBackModalId,
      singleControlId,
      displayConfig,
      dateRange,
    }: UseAuditScanModalType) => {
      if (auditId) {
        setAuditId(auditId)
      }
      setTitle(title)
      setDescription(description)
      setControlsFilter(controlsFilter || [ControlDisplay.ALL])
      setControlsPreselect(controlsPreselect || [ControlDisplay.ALL])
      setDisplayConfig(
        displayConfig || {
          buttonConfig: ButtonConfig.DEFAULT,
          showResults: false,
        },
      )
      if (dateRange) {
        setDateRange(dateRange)
      }
      if (!auditId && !dateRange) {
        setDateRange({
          startDate: dateLimit,
          endDate: new Date(),
        })
      }
      setSingleControlId(singleControlId)
      openModal(ModalFormId.AUDIT_SCAN_MODAL)
      setOnBackModalId(onBackModalId || null)
    },
    [
      setAuditId,
      openModal,
      setControlsFilter,
      setControlsPreselect,
      setDescription,
      setOnBackModalId,
      setTitle,
      setSingleControlId,
      setDisplayConfig,
      setDateRange,
      dateLimit,
    ],
  )
}

export const useRoadmapModal = (): (({
  controlName,
  controlsFilter,
  controlsPreselect,
  singleControlId,
}: Partial<UseAuditScanModalType>) => void) => {
  const auditScanModal = useAuditScanModal(true)
  const { auditId } = useInAudit()

  return useCallback(
    ({
      controlName,
      controlsFilter,
      controlsPreselect,
      singleControlId,
    }: Partial<UseAuditScanModalType>) =>
      auditScanModal({
        title:
          singleControlId && controlName
            ? controlName
            : getRoadmapModalTitle(controlName || 'Audit Readiness'),
        description:
          'Control Test will test these controls. All controls must pass for audit readiness.',
        controlsFilter,
        auditId: auditId || '',
        controlsPreselect,
        singleControlId,
        displayConfig: singleControlId
          ? { buttonConfig: ButtonConfig.NONE, showResults: true }
          : { buttonConfig: ButtonConfig.ALL, showResults: true },
      }),
    [auditScanModal, auditId],
  )
}

export const useAuditBotModal = (): (({
  auditId,
  onBackModalId,
  range,
}: Partial<UseAuditScanModalType> & {
  auditId: string | undefined
  range?: DateRange
}) => void) => {
  const auditScanModal = useAuditScanModal(false)

  return useCallback(
    ({ onBackModalId, auditId, range }) =>
      auditScanModal({
        title: 'Select Controls to Test',
        description: <AuditBotControlsDescription />,
        onBackModalId,
        auditId,
        displayConfig: {
          buttonConfig: ButtonConfig.DEFAULT,
          showResults: false,
        },
        dateRange: range,
      }),
    [auditScanModal],
  )
}

export const filterControlIds = (
  controls: Control[],
  auditReadinessList: AuditReadinessRecord[],
  filters: ControlDisplay[],
): string[] => {
  const auditReadinessMap = normalizeByControlId(auditReadinessList)
  const controlTestMap = createControlTestMap(auditReadinessMap)
  return getFilteredControlIds(controls, controlTestMap, filters)
}

export const useFilteredControlIds = ({
  auditId,
  filters,
}: {
  auditId?: string
  filters: ControlDisplay[]
}): string[] => {
  const { data: controlsData, isLoading: controlsLoading } =
    useControlsWithAuditId(auditId)
  const { data, isLoading } = useAuditReadiness({
    auditId,
    calculateStaleness: true,
  })
  if (isLoading || !data || controlsLoading || !controlsData) return []
  return filterControlIds(
    controlsData.getItemsList(),
    data.getAuditReadinessList(),
    filters,
  )
}
